import { useRef, useState } from 'react';
import { CustomEquipmentType, CustomEquipmentTypes } from '../../../../shared/types/customEquipment.ts';
import { CmmBadge } from '../../../components/cmmBadge.tsx';
import { ModalSubmitDialog } from '../../../components/modalSubmitDialog.tsx';
import { CmmConfig, CustomEquipment } from '../../../types/sharedTypeImpl.ts';
import { trpcClient } from '../../../utils/trpc/trpc.ts';


interface Props {
    equipmentUnderEdit?: CustomEquipment;
    equipments: CustomEquipment[];
    cmmConfigs: CmmConfig[];
    onEquipmentSaved: (equipment: CustomEquipment) => void;
    onClose: () => void;
}

export function CustomEquipmentDialog({
    equipmentUnderEdit,
    equipments,
    cmmConfigs,
    onEquipmentSaved,
    onClose,
}: Props) {
    const formRef = useRef<HTMLFormElement>(null);
    const [name, setName] = useState<string>(equipmentUnderEdit?.name ?? '');
    const [displayName, setDisplayName] = useState<string>(equipmentUnderEdit?.displayName ?? '');
    const [sharepointFolder, setSharepointFolder] = useState<string>(equipmentUnderEdit?.sharepointFolder ?? '');
    const [type, setType] = useState<string>(equipmentUnderEdit?.type ?? '');
    const [cmmConfigId, setCmmConfigId] = useState(equipmentUnderEdit?.cmmConfigId ?? '');
    const [badgeText, setBadgeText] = useState(equipmentUnderEdit?.badgeText ?? '');
    const [badgeColor, setBadgeColor] = useState(equipmentUnderEdit?.badgeColor ?? '#2563eb');
    const isCmmEquipment = type === 'cmm' || type === 'vmm';


    function handleSubmitClicked() {
        if (formRef.current) {
            formRef.current.requestSubmit();
        }
    }

    async function handleSubmit() {
        const isNew = !equipmentUnderEdit;
        if (isNew && equipments.some(it => it.name.toUpperCase() === name.toUpperCase())) {
            alert('Name must be unique');
            return;
        }

        const equipment = {
            ...(equipmentUnderEdit && { _id: equipmentUnderEdit._id! }),
            name: name.trim(),
            displayName: displayName.trim(),
            sharepointFolder: sharepointFolder.trim(),
            type: type as CustomEquipmentType,
            cmmConfigId: isCmmEquipment && cmmConfigId ? cmmConfigId : undefined,
            badgeText: isCmmEquipment ? badgeText.trim() || undefined : undefined,
            badgeColor: isCmmEquipment ? badgeColor : undefined,
            isActive: true,
        }

        try {
            const _result = isNew
                ? await trpcClient.equipments.createCustom.mutate(equipment)
                : await trpcClient.equipments.updateCustom.mutate(equipment);
            onEquipmentSaved(equipment);
            onClose();
        } catch (error) {
            alert('Failed to save equipment');
        }
    }

    return (
        <ModalSubmitDialog
            frameStyle={{ width: '360px' }}
            isOpen={true}
            onCancel={onClose}
            onSubmit={handleSubmitClicked}
        >
            <form
                ref={formRef}
                onSubmit={e => { e.preventDefault(); handleSubmit(); }}
            >
                <div>
                    <div>Id</div>
                    <input
                        className='dialog-form__input'
                        type='text'
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                    />
                </div>
                <div className='dialog-form__field--with-margins' >
                    <div>Display name</div>
                    <input
                        className='dialog-form__input'
                        type='text'
                        value={displayName}
                        onChange={e => setDisplayName(e.target.value)}
                        required
                    />
                </div>
                <div className='dialog-form__field--with-margins' >
                    <div>Sharepoint folder</div>
                    <input
                        className='dialog-form__input'
                        type='text'
                        value={sharepointFolder}
                        onChange={e => setSharepointFolder(e.target.value)}
                    />
                </div>
                <div className='dialog-form__field--with-margins' >
                    <div>Type</div>
                    <select
                        className='dialog-form__input'
                        value={type}
                        onChange={e => setType(e.target.value)}
                        required
                    >
                        <option value={''} disabled hidden />
                        {CustomEquipmentTypes.map(option =>
                            <option key={option.value} value={option.value}>{option.label}</option>
                        )}
                    </select>
                </div>
                {isCmmEquipment && (
                    <div className='dialog-form__field--with-margins'>
                        <div>CMM configuration</div>
                        <select
                            className='dialog-form__input'
                            value={cmmConfigId}
                            onChange={e => setCmmConfigId(e.target.value)}
                        >
                            <option value=''>None</option>
                            {cmmConfigs.map(config => (
                                <option key={config._id} value={config._id}>{config.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                {isCmmEquipment && (
                    <div className='dialog-form__field--with-margins'>
                        <div>Badge</div>
                        <div className='cmm-badge-editor'>
                            <label className='cmm-badge-editor__text'>
                                <span>Text</span>
                                <input
                                    value={badgeText}
                                    onChange={e => setBadgeText(e.target.value)}
                                />
                            </label>
                            <label>
                                <span>Colour</span>
                                <input
                                    className='cmm-badge-editor__color'
                                    type='color'
                                    value={badgeColor}
                                    onChange={e => setBadgeColor(e.target.value)}
                                />
                            </label>
                            <div className='cmm-badge-editor__preview' aria-label='Badge preview'>
                                <span>Preview</span>
                                <CmmBadge
                                    badgeText={badgeText}
                                    badgeColor={badgeColor}
                                    displayName={displayName}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </ModalSubmitDialog>
    );
}
